"use client";

import ButtonLogin from "@/components/ButtonLogin";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { GraduationCap, Menu, Target, UserSearch } from "lucide-react";
import config from "@/config";

const solutions = [
  { key: "hiring", href: "/solutions/hiring", icon: UserSearch },
  {
    key: "onboarding",
    href: "/solutions/onboarding-certification",
    icon: GraduationCap,
  },
  { key: "call_prep", href: "/solutions/call-preparation", icon: Target },
] as const;

export type NavBarStrings = {
  logoAlt: string;
  solutions: string;
  demoButton: string;
  loginButton: string;
  menuLabel: string;
  items: Record<
    (typeof solutions)[number]["key"],
    { title: string; description: string }
  >;
};

export default function NavBar({ strings }: { strings: NavBarStrings }) {
  const router = useRouter();
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <nav className="fixed inset-x-0 top-0 z-50 border-b border-[#d9dfd8] bg-[#f8f9f5]/95 backdrop-blur-md">
      <div className="mx-auto flex h-[76px] w-full max-w-[1360px] items-center justify-between px-5 sm:px-8 lg:px-10">
        <div className="flex items-center gap-8">
          <Link href="/" className="shrink-0">
            <Image
              src="/logo/logo-dark.png"
              alt={strings.logoAlt}
              width={142}
              height={26}
              className="h-auto w-[132px] sm:w-[142px]"
            />
          </Link>

          {/* Desktop navigation */}
          <NavigationMenu className="hidden md:flex">
            <NavigationMenuList>
              <NavigationMenuItem>
                <NavigationMenuTrigger className="rounded-none bg-transparent hover:bg-[#eef4ea] focus:bg-[#eef4ea] data-[state=open]:bg-[#eef4ea]">
                  {strings.solutions}
                </NavigationMenuTrigger>
                <NavigationMenuContent>
                  <ul className="grid w-[420px] gap-1 p-2">
                    {solutions.map(({ key, href, icon: Icon }) => (
                      <li key={key}>
                        <NavigationMenuLink asChild>
                          <Link
                            href={href}
                            className="flex items-start gap-3 rounded-none p-3 outline-none transition-colors hover:bg-[#eef4ea] focus:bg-[#eef4ea]"
                          >
                            <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center bg-[#dff7c9] text-[#26734a]">
                              <Icon className="h-5 w-5" />
                            </span>
                            <span>
                              <span className="block text-sm font-medium text-gray-900">
                                {strings.items[key].title}
                              </span>
                              <span className="block text-sm text-gray-500">
                                {strings.items[key].description}
                              </span>
                            </span>
                          </Link>
                        </NavigationMenuLink>
                      </li>
                    ))}
                  </ul>
                </NavigationMenuContent>
              </NavigationMenuItem>
            </NavigationMenuList>
          </NavigationMenu>
        </div>

        <div className="flex items-center">
          <div className="hidden md:block">
            <ButtonLogin text={strings.loginButton} />
          </div>
          <Button
            size={"lg"}
            onClick={() => router.push(config.demoUrl)}
            className="ml-3 hidden h-8 rounded-none bg-[#0d1711] px-5 text-white hover:bg-[#26734a] md:inline-flex"
          >
            {strings.demoButton}
          </Button>

          {/* Mobile menu */}
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 md:hidden"
                aria-label={strings.menuLabel}
              >
                <Menu className="h-6 w-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[320px] rounded-none">
              <SheetHeader>
                <SheetTitle className="text-left text-sm font-medium text-gray-500">
                  {strings.solutions}
                </SheetTitle>
              </SheetHeader>
              <div className="mt-2 flex flex-col gap-1">
                {solutions.map(({ key, href, icon: Icon }) => (
                  <Link
                    key={key}
                    href={href}
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center gap-3 p-3 transition-colors hover:bg-[#eef4ea]"
                  >
                    <Icon className="h-5 w-5 text-[#26734a]" />
                    <span className="text-sm font-medium text-gray-900">
                      {strings.items[key].title}
                    </span>
                  </Link>
                ))}
              </div>
              <div className="mt-6 flex flex-col gap-3 border-t pt-6">
                <ButtonLogin text={strings.loginButton} />
                <Button
                  size={"lg"}
                  className="h-8 rounded-none bg-[#0d1711] hover:bg-[#26734a]"
                  onClick={() => {
                    setMobileOpen(false);
                    router.push(config.demoUrl);
                  }}
                >
                  {strings.demoButton}
                </Button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </nav>
  );
}
